(() => {
  const money = (cents) => {
    if (window.Shopify?.currency?.active && window.Intl) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: Shopify.currency.active
      }).format(cents / 100);
    }
    return `${(cents / 100).toFixed(2)}`;
  };

  class WyTabs extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => this.select(index));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % this.buttons.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.buttons.length) % this.buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.buttons.length - 1;
          this.select(next, true);
        });
      });
    }
    select(index, focus = false) {
      this.buttons.forEach((button, i) => {
        const active = i === index;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        this.panels[i].hidden = !active;
      });
      if (focus) this.buttons[index].focus();
    }
  }

  class WyBeforeAfter extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      if (!range) return;
      const update = () => {
        this.style.setProperty('--wy-position', `${range.value}%`);
        range.setAttribute('aria-valuetext', `${range.value}% before, ${100 - range.value}% after`);
      };
      range.addEventListener('input', update);
      update();
    }
  }

  class WyCountdown extends HTMLElement {
    connectedCallback() {
      this.duration = Number(this.dataset.duration || 10) * 60 * 1000;
      this.mode = this.dataset.mode || 'session';
      this.storageKey = `wy-countdown-${this.dataset.id || 'global'}`;
      this.end = this.resolveEnd();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    resolveEnd() {
      if (this.mode === 'daily') {
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        return end.getTime();
      }
      const stored = Number(sessionStorage.getItem(this.storageKey));
      if (stored > Date.now()) return stored;
      const end = Date.now() + this.duration;
      sessionStorage.setItem(this.storageKey, String(end));
      return end;
    }
    tick() {
      let left = Math.max(0, this.end - Date.now());
      if (!left && this.mode === 'session') {
        this.end = Date.now() + this.duration;
        sessionStorage.setItem(this.storageKey, String(this.end));
        left = this.duration;
      }
      const hours = Math.floor(left / 3600000);
      const minutes = Math.floor((left % 3600000) / 60000);
      const seconds = Math.floor((left % 60000) / 1000);
      const set = (part, value) => {
        const node = this.querySelector(`[data-${part}]`);
        if (node) node.textContent = String(value).padStart(2, '0');
      };
      set('hours', hours); set('minutes', minutes); set('seconds', seconds);
      const compact = this.querySelector('[data-compact-time]');
      if (compact) compact.textContent = `${String(minutes + hours * 60).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
  }

  class WyCounter extends HTMLElement {
    connectedCallback() {
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return this.finish();
      this.observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        this.animate(); this.observer.disconnect();
      }, { threshold: .35 });
      this.observer.observe(this);
    }
    finish() { this.querySelectorAll('[data-count]').forEach(el => el.textContent = el.dataset.count); }
    animate() {
      this.querySelectorAll('[data-count]').forEach(el => {
        const target = Number(el.dataset.count || 0);
        const decimals = Number(el.dataset.decimals || 0);
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min(1, (now - start) / 1100);
          const eased = 1 - Math.pow(1 - progress, 3);
          el.textContent = (target * eased).toFixed(decimals);
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }
  }

  class WyStickyAtc extends HTMLElement {
    connectedCallback() {
      this.trigger = document.querySelector('.product-form__buttons');
      this.mainForm = document.querySelector('product-form form');
      this.button = this.querySelector('[data-wy-sticky-submit]');
      if (!this.trigger || !this.mainForm || !this.button) return this.remove();
      this.button.addEventListener('click', () => {
        const submit = this.mainForm.querySelector('[type="submit"]');
        submit?.click();
      });
      this.observer = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      }, { rootMargin: '-80px 0px 0px' });
      this.observer.observe(this.trigger);
    }
  }

  class WyCartTimer extends WyCountdown {}

  const definitions = {
    'wy-tabs': WyTabs,
    'wy-before-after': WyBeforeAfter,
    'wy-countdown': WyCountdown,
    'wy-counter': WyCounter,
    'wy-sticky-atc': WyStickyAtc,
    'wy-cart-timer': WyCartTimer,
  };
  Object.entries(definitions).forEach(([name, component]) => {
    if (!customElements.get(name)) customElements.define(name, component);
  });

  window.WakeoryDTC = { money };
})();
